import React, {useEffect, useState} from "react";
import map from "lodash/map";
import moment, {Moment} from "moment";
import {Row, Col, Button, Form, Select, DatePicker, Skeleton} from "antd";
import {useCustomerAccountsWithTransactions, useLoanCycles} from "@/hooks/accounts/useCustomerAccount";
import {showWarningNotification} from "@/components/common/utils/notify";
import PdfMakeModal from "@/components/common/PdfMakeModal";
import PdfLegerCardModal from "@/components/common/PdfLegerCardModal";
import {useForm} from "antd/es/form/Form";
import {AccountRequestDto, AuthUser, LoanStatementType, ProductDTO} from "@/shared/api/model";

const {Option} = Select;

interface Account {
    glNo: string;
    productName: string;
}

interface FormValues {
    product: string;
    startDate: Moment;
    endDate: Moment;
}

interface AccountStatementFormProps {
    authUser: AuthUser;
}

const AccountStatementForm: React.FC<AccountStatementFormProps> = ({authUser}) => {
    const [show, setShow] = useState(false);
    const [statementRequest, setStatementRequest] = useState<AccountRequestDto | null>(null);
    const [loanStatementType, setLoanStatementType] = useState<LoanStatementType>(LoanStatementType.LEDGER_CARD);
    const [ledgerCardRequest, setLedgerCardRequest] = useState<AccountRequestDto | null>(null);
    const [selectedCycle, setSelectedCycle] = useState<number | undefined>(undefined);
    const mq = window.matchMedia("(max-width: 500px)");
    const isMobile = mq.matches;
    // @ts-ignore
    const [customerAccountQuery] = useCustomerAccountsWithTransactions(authUser.branch, authUser.custNo);
    const [selectedProduct, setSelectedProduct] = useState<ProductDTO | undefined>(undefined);
    const {isLoading, isError, data, error} = customerAccountQuery;
    const [form] = useForm();
    
    // Fetch loan cycles when a loan product is selected
    // @ts-ignore
    const [loanCyclesQuery] = useLoanCycles(    // @ts-ignore
// @ts-ignore
        authUser.branch,
        authUser.custNo,
        selectedProduct?.glNo || "",
        selectedProduct?.product === 'L'
    );
    const {data: loanCycles, isLoading: cyclesLoading} = loanCyclesQuery;


    if (isLoading) return <Skeleton active/>;

    // Add debug logging to help diagnose issues
    console.log('AccountStatementForm data:', data);
    
    if (isError) {
        console.error('AccountStatementForm error:', error);
        return <div>Error loading account information. Please try again later.</div>;
    }
    
    if (!data || data.length === 0) {
        return <div>No account information available.</div>;
    }

    const onFinish = (values: FormValues) => {
        const {product, startDate, endDate} = values;

        if (selectedProduct?.product === 'S' || selectedProduct?.product === 'H') {

            if (!values.startDate || !values.endDate) {
                showWarningNotification("Warning!", "Please select a date range");
                return;
            }


            const duration = moment.duration(endDate.diff(startDate));
            if (duration.asYears() > 10) {
                showWarningNotification("Warning!", "Date range should not exceed 10 years");
                return;
            }
        }


        if (selectedProduct?.product === 'S' || selectedProduct?.product === 'H') {
            setStatementRequest({
                branch: authUser.branch,
                custNo: authUser.custNo,
                glNo: product,
                fromDate: startDate.format("YYYY-MM-DD"),
                toDate: endDate.format("YYYY-MM-DD"),
            });
        } else {

            setLedgerCardRequest({
                branch: authUser.branch,
                custNo: authUser.custNo,
                glNo: product,
                loanStatementType,
                cycle: selectedCycle
            });

        }


        handleShow(true);
    };


    const handleStatementType = (statementType: LoanStatementType) => {
        setLoanStatementType(statementType)
    }

    const handleSelected = (selected: string) => {
        if (!Array.isArray(data)) {
            console.error('Data is not an array');
            return;
        }

        const _selectedProduct: ProductDTO | undefined = data.find(product => product.glNo === selected);

        if (_selectedProduct) {
            setSelectedProduct(_selectedProduct);
            // Reset cycle selection when product changes
            setSelectedCycle(undefined);
            form.setFieldsValue({cycle: undefined});
        } else {
            console.warn(`No product found with glNo: ${selected}`);
            setSelectedProduct(undefined);
        }

        form.setFieldsValue({loanStatementType: LoanStatementType.LEDGER_CARD});
    };

    const handleShow = (visible: boolean) => {
        setShow(visible);
    };

    return (
        <>
            <Form
                labelCol={{span: 24}}
                wrapperCol={{span: 24}}
                autoComplete="off"
                onFinish={onFinish}
                form={form}
            >
                {statementRequest && (
                    <PdfMakeModal
                        statementRequest={statementRequest}
                        show={show}
                        title="Account Statement"
                        handleShow={handleShow}
                    />
                )}

                {ledgerCardRequest && (
                    <PdfLegerCardModal
                        statementRequest={ledgerCardRequest}
                        show={show}
                        title="Account Statement"
                        handleShow={handleShow}
                    />
                )}

                <Row gutter={[24, 0]} justify="space-between">
                    <Col span={24}>
                        <Form.Item
                            name="product"
                            label="Product"
                            rules={[{required: true, message: "Product is required"}]}
                        >
                            <Select placeholder="Select Product" size="large" onChange={handleSelected} allowClear>
                                {map(data, (account: Account) => (
                                    <Option value={account.glNo} key={account.glNo}>
                                        {account.productName}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    {selectedProduct && selectedProduct.product === 'L' &&
                        (
                            <>
                                <Col span={24}>
                                    <Form.Item
                                        name="loanStatementType"
                                        label="Statement Type"
                                        rules={[{required: true, message: "Statement Type is required"}]}
                                    >
                                        <Select placeholder="Statement Type" size="large" onChange={handleStatementType}>
                                            <Option value={LoanStatementType.LEDGER_CARD} key={LoanStatementType.LEDGER_CARD}>
                                                Ledger Card
                                            </Option>
                                            <Option value={LoanStatementType.REPAYMENT_SCHEDULE} key={LoanStatementType.REPAYMENT_SCHEDULE}>
                                                Repayment Plan
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item
                                        name="cycle"
                                        label="Loan Cycle"
                                        help={cyclesLoading ? "Loading cycles..." : loanCycles && loanCycles.length > 0 ? `Latest cycle: ${loanCycles[0]}` : ""}
                                    >
                                        <Select 
                                            placeholder="Select Cycle (defaults to latest)" 
                                            size="large" 
                                            onChange={(value) => setSelectedCycle(value)}
                                            loading={cyclesLoading}
                                            allowClear
                                        >
                                            {loanCycles && loanCycles.map((cycle: number) => (
                                                <Option value={cycle} key={cycle}>
                                                    Cycle {cycle} {cycle === loanCycles[0] ? '(Latest)' : ''}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </>
                        )
                    }


                    {selectedProduct && selectedProduct.product !== 'L' &&
                        (
                            <>
                                <Col span={24}>
                                    <Form.Item name="startDate" label="Start Date">
                                        <DatePicker
                                            size="large"
                                            style={{width: "100%"}}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item name="endDate" label="End Date">
                                        <DatePicker
                                            size="large"
                                            style={{width: "100%"}}
                                        />
                                    </Form.Item>
                                </Col>
                            </>

                        )


                    }


                </Row>
                <Form.Item wrapperCol={{span: 24}} style={{textAlign: "right"}}>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={isLoading}
                    >
                        {isMobile ? "Download" : "Preview"}
                    </Button>
                </Form.Item>
            </Form>
        </>
    );
};

export default AccountStatementForm;